import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, MessageCircle, Calendar, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-371">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-372" />
    </svg>;
}
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesHovered, setIsServicesHovered] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const {
    t,
    lang
  } = useTranslation();
  type L = {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  const pick = (o: L) => o[lang as 'en' | 'ua' | 'ru' | 'ro'] || o.en;
  const megaMenuColumns: {
    title: L;
    finance?: boolean;
    items: {
      name: L;
      href: string;
    }[];
  }[] = [{
    title: {
      en: 'General Dentistry',
      ua: 'Загальна стоматологія',
      ru: 'Общая стоматология',
      ro: 'Stomatologie Generală'
    },
    items: [{
      name: {
        en: 'Dental Examination',
        ua: 'Стоматологічний огляд',
        ru: 'Стоматологический осмотр',
        ro: 'Examinare Dentară'
      },
      href: `/${lang}/prices#consultations`
    }, {
      name: {
        en: 'Emergency Dentist',
        ua: 'Екстрена стоматологія',
        ru: 'Неотложная стоматология',
        ro: 'Stomatolog de Urgență'
      },
      href: `/${lang}/emergency-dentist-london`
    }, {
      name: {
        en: 'Dental Hygiene',
        ua: 'Гігієна порожнини рота',
        ru: 'Гигиена полости рта',
        ro: 'Igienă Dentară'
      },
      href: `/${lang}/services/hygiene`
    }, {
      name: {
        en: 'Fillings & Restorations',
        ua: 'Пломби та реставрації',
        ru: 'Пломбы и реставрации',
        ro: 'Plombe și Restaurări'
      },
      href: `/${lang}/services/fillings`
    }, {
      name: {
        en: 'Root Canal Treatment',
        ua: 'Лікування каналів',
        ru: 'Лечение каналов',
        ro: 'Tratament de Canal'
      },
      href: `/${lang}/services/root-canal`
    }, {
      name: {
        en: 'Tooth Extraction',
        ua: 'Видалення зубів',
        ru: 'Удаление зубов',
        ro: 'Extracție Dentară'
      },
      href: `/${lang}/services/extraction`
    }]
  }, {
    title: {
      en: 'Cosmetic Dentistry',
      ua: 'Естетична стоматологія',
      ru: 'Эстетическая стоматология',
      ro: 'Stomatologie Cosmetică'
    },
    finance: true,
    items: [{
      name: {
        en: 'Teeth Whitening',
        ua: 'Відбілювання зубів',
        ru: 'Отбеливание зубов',
        ro: 'Albirea Dinților'
      },
      href: `/${lang}/services/whitening`
    }, {
      name: {
        en: 'Composite Bonding',
        ua: 'Композитний бондинг',
        ru: 'Композитный бондинг',
        ro: 'Bonding Compozit'
      },
      href: `/${lang}/prices#fillings-restorations`
    }, {
      name: {
        en: 'Veneers',
        ua: 'Вініри',
        ru: 'Виниры',
        ro: 'Fațete'
      },
      href: `/${lang}/veneers-london`
    }, {
      name: {
        en: 'Crowns',
        ua: 'Коронки',
        ru: 'Коронки',
        ro: 'Coroane'
      },
      href: `/${lang}/services/crowns`
    }, {
      name: {
        en: 'Smile Makeover',
        ua: 'Перетворення усмішки',
        ru: 'Преображение улыбки',
        ro: 'Transformarea Zâmbetului'
      },
      href: `/${lang}/services/smile-makeover`
    }]
  }, {
    title: {
      en: 'Orthodontics',
      ua: 'Ортодонтія',
      ru: 'Ортодонтия',
      ro: 'Ortodonție'
    },
    finance: true,
    items: [{
      name: {
        en: 'Orthodontics Overview',
        ua: 'Огляд ортодонтії',
        ru: 'Обзор ортодонтии',
        ro: 'Prezentare Ortodonție'
      },
      href: `/${lang}/services/orthodontics`
    }, {
      name: {
        en: 'Clear Aligners',
        ua: 'Прозорі елайнери',
        ru: 'Прозрачные элайнеры',
        ro: 'Aligneri Transparenți'
      },
      href: `/${lang}/services/orthodontics/clear-aligners`
    }, {
      name: {
        en: 'Invisalign-style Aligners',
        ua: 'Елайнери типу Invisalign',
        ru: 'Элайнеры типа Invisalign',
        ro: 'Aligneri tip Invisalign'
      },
      href: `/${lang}/services/orthodontics/invisalign-style-aligners`
    }, {
      name: {
        en: 'Metal Braces',
        ua: 'Металеві брекети',
        ru: 'Металлические брекеты',
        ro: 'Aparat Dentar Metalic'
      },
      href: `/${lang}/services/orthodontics/metal-braces`
    }, {
      name: {
        en: 'Ceramic Braces',
        ua: 'Керамічні брекети',
        ru: 'Керамические брекеты',
        ro: 'Aparat Dentar Ceramic'
      },
      href: `/${lang}/services/orthodontics/ceramic-braces`
    }, {
      name: {
        en: 'Retainers',
        ua: 'Ретейнери',
        ru: 'Ретейнеры',
        ro: 'Retaineri'
      },
      href: `/${lang}/services/orthodontics/retainers`
    }, {
      name: {
        en: 'Children’s Orthodontics',
        ua: 'Дитяча ортодонтія',
        ru: 'Детская ортодонтия',
        ro: 'Ortodonție Pediatrică'
      },
      href: `/${lang}/services/orthodontics/children`
    }]
  }, {
    title: {
      en: 'Advanced Treatments',
      ua: 'Складні методи лікування',
      ru: 'Сложные методы лечения',
      ro: 'Tratamente Avansate'
    },
    finance: true,
    items: [{
      name: {
        en: 'Dental Implants',
        ua: 'Імплантація зубів',
        ru: 'Имплантация зубов',
        ro: 'Implanturi Dentare'
      },
      href: `/${lang}/implanty-london`
    }, {
      name: {
        en: 'Oral Surgery',
        ua: 'Хірургічна стоматологія',
        ru: 'Хирургическая стоматология',
        ro: 'Chirurgie Orală'
      },
      href: `/${lang}/prices#extractions`
    }, {
      name: {
        en: 'CBCT / 3D Scan',
        ua: 'КТ / 3D-сканування',
        ru: 'КТ / 3D-сканирование',
        ro: 'CBCT / Scanare 3D'
      },
      href: `/${lang}/prices#radiography`
    }, {
      name: {
        en: 'Digital Dentistry',
        ua: 'Цифрова стоматологія',
        ru: 'Цифровая стоматология',
        ro: 'Stomatologie Digitală'
      },
      href: `/${lang}/about`
    }, {
      name: {
        en: 'Microscopic Dentistry',
        ua: 'Мікроскопічна стоматологія',
        ru: 'Микроскопическая стоматология',
        ro: 'Stomatologie Microscopică'
      },
      href: `/${lang}/about`
    }]
  }];
  const menuStrings = {
    promoTitle: {
      en: 'Clear Aligners from £2,995',
      ua: 'Прозорі елайнери від £2,995',
      ru: 'Прозрачные элайнеры от £2,995',
      ro: 'Aligneri transparenți de la £2,995'
    } as L,
    promoSubtitle: {
      en: 'Discreet teeth straightening in Angel, London.',
      ua: 'Непомітне вирівнювання зубів в Angel, Лондон.',
      ru: 'Незаметное выравнивание зубов в Angel, Лондон.',
      ro: 'Îndreptarea discretă a dinților în Angel, Londra.'
    } as L,
    bookConsultation: {
      en: 'Book Consultation',
      ua: 'Записатися на консультацію',
      ru: 'Записаться на консультацию',
      ro: 'Programare Consultație'
    } as L,
    finance: {
      en: '0% finance available for up to 12 months on selected treatments.',
      ua: '0% фінансування до 12 місяців на окремі види лікування.',
      ru: '0% рассрочка до 12 месяцев на отдельные виды лечения.',
      ro: 'Finanțare 0% disponibilă până la 12 luni pentru anumite tratamente.'
    } as L,
    askFinance: {
      en: 'Ask About Finance',
      ua: 'Дізнатися про розстрочку',
      ru: 'Узнать о рассрочке',
      ro: 'Întreabă despre Finanțare'
    } as L
  };
  const navLabels = {
    patientExperience: {
      en: 'Patient Experience',
      ua: 'Досвід пацієнта',
      ru: 'Опыт пациента',
      ro: 'Experiența Pacientului'
    } as L,
    results: {
      en: 'Results',
      ua: 'Результати',
      ru: 'Результаты',
      ro: 'Rezultate'
    } as L,
    blog: {
      en: 'Blog',
      ua: 'Блог',
      ru: 'Блог',
      ro: 'Blog'
    } as L
  };
  const navigation = [{
    name: t('nav.about'),
    href: `/${lang}/about`
  }, {
    name: t('nav.services'),
    href: `/${lang}/services`
  }, {
    name: t('nav.prices'),
    href: `/${lang}/prices`
  }, {
    name: pick(navLabels.patientExperience),
    href: `/${lang}/patient-experience`
  }, {
    name: pick(navLabels.results),
    href: `/${lang}/results`
  }, {
    name: pick(navLabels.blog),
    href: `/${lang}/blog`
  }, {
    name: t('nav.contact'),
    href: `/${lang}/contact`
  }];
  return <header className="sticky top-0 z-50 w-full bg-ivory/85 backdrop-blur-md border-b border-stone-200" onMouseLeave={() => setIsServicesHovered(false)} data-id="element-373">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-374">
        <div className="flex justify-between items-center h-20" data-id="element-375">
          {/* Logo — minimal architectural wordmark */}
          <Link to={`/${lang}`} className="flex flex-col leading-none" data-id="element-376">
            <span className="text-2xl font-semibold tracking-[0.22em] text-forest-700" style={{
            fontFamily: '"Playfair Display", Georgia, serif'
          }} data-id="element-377">
              DENTICO
            </span>
            <span className="block text-right text-[10px] tracking-[0.4em] text-forest-700 uppercase mt-0.5" data-id="element-378">
              Clinic
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 h-full" data-id="element-379">
            {navigation.map(item => {
            if (item.name === t('nav.services')) {
              return <div key={item.href} className="h-full flex items-center" onMouseEnter={() => setIsServicesHovered(true)} data-id="element-380">
                    <Link to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors flex items-center gap-1" data-id="element-381">
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesHovered ? 'rotate-180 text-forest-600' : ''}`} data-id="element-382" />
                    </Link>
                  </div>;
            }
            if (item.href.endsWith('/patient-experience')) {
              const words = item.name.split(' ');
              return <Link key={item.href} to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors flex flex-col items-center text-center leading-[1.1]" data-id="element-383">
                    {words.map((w, i) => <span key={i} data-id="element-384">{w}</span>)}
                  </Link>;
            }
            if (item.href.includes('/patient-experience')) {
              return <Link key={item.href} to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors flex flex-col items-center justify-center text-center leading-[1.15]" data-id="element-385">
                    {item.name.split(' ').map((word, i) => <span key={i} data-id="element-386">{word}</span>)}
                  </Link>;
            }
            return <Link key={item.href} to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors" data-id="element-387">
                  {item.name}
                </Link>;
          })}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4" data-id="element-388">
            <LanguageSwitcher data-id="element-389" />
            <div className="h-6 w-px bg-beige/60" data-id="element-390" />
            <a href="tel:+447345197820" className="text-stone-500 hover:text-forest-600 transition-colors" aria-label="Phone" data-id="element-391">
              <Phone className="w-5 h-5" data-id="element-392" />
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-forest-600 transition-colors" aria-label="WhatsApp" data-id="element-393">
              <MessageCircle className="w-5 h-5" data-id="element-394" />
            </a>
            <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-forest-600 transition-colors" aria-label="Telegram" data-id="element-395">
              <TelegramIcon className="w-5 h-5" data-id="element-396" />
            </a>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-397">
              <Button size="sm" className="gap-2" data-id="element-398">
                <Calendar className="w-4 h-4" data-id="element-399" />
                {t('nav.bookOnline')}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2 flex-shrink-0" data-id="element-400">
            <LanguageSwitcher data-id="element-401" />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="relative z-50 flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 text-charcoal hover:text-forest-700 active:bg-stone-100 rounded-lg transition-colors" aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} type="button" data-id="element-402">
              {isMenuOpen ? <X className="w-6 h-6" data-id="element-403" /> : <Menu className="w-6 h-6" data-id="element-404" />}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Mega Menu Dropdown — compact, attached, subtle */}
      <div className={`hidden md:block absolute top-full left-0 w-full bg-ivory border-t border-stone-200 shadow-[0_14px_28px_-18px_rgba(47,74,63,0.22)] transition-all duration-200 origin-top ${isServicesHovered ? 'opacity-100 scale-y-100 visible' : 'opacity-0 scale-y-95 invisible'}`} onMouseEnter={() => setIsServicesHovered(true)} onMouseLeave={() => setIsServicesHovered(false)} data-id="element-405">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-406">
          <div className="flex gap-7" data-id="element-407">
            {/* Columns */}
            <div className="flex-grow grid grid-cols-4 gap-5 divide-x divide-stone-200/70" data-id="element-408">
              {megaMenuColumns.map((col, idx) => <div key={idx} className={`flex flex-col ${idx > 0 ? 'pl-5' : ''}`} data-id="element-409">
                  <h3 className="text-forest-700 font-semibold mb-3 text-[11px] uppercase tracking-[0.15em]" style={{
                fontFamily: '"DM Sans", sans-serif'
              }} data-id="element-410">
                    {pick(col.title)}
                  </h3>
                  <ul className="space-y-1" data-id="element-411">
                    {col.items.map((link, linkIdx) => <li key={linkIdx} data-id="element-412">
                        <Link to={link.href} className="text-sm text-stone-600 hover:text-forest-700 hover:bg-stone-100/70 -mx-2 px-2 py-1 rounded-md transition-colors block" onClick={() => setIsServicesHovered(false)} data-id="element-413">
                          {pick(link.name)}
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>

            {/* Promo Card — compact & elegant */}
            <div className="w-[230px] flex-shrink-0" data-id="element-414">
              <div className="bg-forest-900 border border-white/10 rounded-xl p-4 h-full flex flex-col justify-between" data-id="element-415">
                <div data-id="element-416">
                  <h4 className="text-base font-semibold text-ivory mb-1.5 leading-snug" data-id="element-417">
                    {pick(menuStrings.promoTitle)}
                  </h4>
                  <p className="text-xs text-ivory/70 mb-3 leading-relaxed" data-id="element-418">
                    {pick(menuStrings.promoSubtitle)}
                  </p>
                </div>
                <div className="space-y-2" data-id="element-419">
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-420">
                    <Button size="sm" fullWidth className="bg-[#F8F5F0] text-[#2F4A3F] border border-[#F8F5F0] hover:bg-[#EFE8DE] hover:text-[#2F4A3F] shadow-sm font-medium" data-id="element-421">
                      {pick(menuStrings.bookConsultation)}
                    </Button>
                  </a>
                  <Link to={`/${lang}/contact`} onClick={() => setIsServicesHovered(false)} data-id="element-422">
                    <Button size="sm" fullWidth className="bg-transparent border border-[#F8F5F0] text-[#F8F5F0] hover:bg-[#F8F5F0] hover:text-[#2F4A3F]" data-id="element-423">
                      {pick(menuStrings.askFinance)}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          {/* Full-width finance line */}
          <div className="border-t border-[#D8C4A8]/70 bg-[#EFE8DE]" data-id="element-424">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-center gap-4" data-id="element-425">
              <p className="text-[13px] leading-snug text-[#2F4A3F] text-center" data-id="element-426">
                <span className="font-semibold" data-id="element-427">0%</span>{' '}
                {pick(menuStrings.finance).replace(/^0% ?/i, '')}
              </p>
              <Link to={`/${lang}/contact`} onClick={() => setIsServicesHovered(false)} className="hidden lg:inline-flex items-center text-[13px] font-medium text-[#2F4A3F] underline decoration-[#D8C4A8] underline-offset-4 hover:decoration-[#2F4A3F] transition-colors whitespace-nowrap" data-id="element-428">
                {pick(menuStrings.askFinance)}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && <div className="md:hidden border-t border-beige/60 bg-ivory relative z-50 max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-429">
          <div className="px-4 py-6 space-y-4" data-id="element-430">
            <nav className="flex flex-col space-y-4" data-id="element-431">
              {navigation.map(item => {
            if (item.name === t('nav.services')) {
              return <div key={item.href} className="flex flex-col space-y-3" data-id="element-432">
                      <div className="flex items-center justify-between" data-id="element-433">
                        <Link to={item.href} className="text-lg font-medium text-charcoal" onClick={() => setIsMenuOpen(false)} data-id="element-434">
                          {item.name}
                        </Link>
                        <button onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)} className="p-2 -mr-2 text-charcoal-light" data-id="element-435">
                          <ChevronDown className={`w-5 h-5 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-436" />
                        </button>
                      </div>
                      {isMobileServicesOpen && <div className="pl-4 space-y-6 pb-2 border-l-2 border-stone-200 ml-1" data-id="element-437">
                          {megaMenuColumns.map((col, idx) => <div key={idx} data-id="element-438">
                              <h3 className="text-forest-700 font-semibold mb-3 text-xs uppercase tracking-[0.15em]" style={{
                      fontFamily: '"DM Sans", sans-serif'
                    }} data-id="element-439">
                                {pick(col.title)}
                              </h3>
                              <ul className="space-y-3" data-id="element-440">
                                {col.items.map((link, linkIdx) => <li key={linkIdx} data-id="element-441">
                                    <Link to={link.href} className="text-base text-stone-600 block" onClick={() => setIsMenuOpen(false)} data-id="element-442">
                                      {pick(link.name)}
                                    </Link>
                                  </li>)}
                              </ul>
                            </div>)}
                          <div className="mt-6 bg-forest-900 border border-white/10 rounded-xl p-5" data-id="element-443">
                            <h4 className="font-semibold text-ivory mb-1" data-id="element-444">
                              {pick(menuStrings.promoTitle)}
                            </h4>
                            <p className="text-sm text-ivory/70 mb-4" data-id="element-445">
                              {pick(menuStrings.promoSubtitle)}
                            </p>
                            <div className="space-y-2" data-id="element-446">
                              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-447">
                                <Button size="sm" fullWidth className="bg-[#F8F5F0] text-[#2F4A3F] border border-[#F8F5F0] hover:bg-[#EFE8DE] hover:text-[#2F4A3F] font-medium" data-id="element-448">
                                  {pick(menuStrings.bookConsultation)}
                                </Button>
                              </a>
                              <Link to={`/${lang}/contact`} onClick={() => setIsMenuOpen(false)} data-id="element-449">
                                <Button size="sm" fullWidth className="bg-transparent border border-[#F8F5F0] text-[#F8F5F0] hover:bg-[#F8F5F0] hover:text-[#2F4A3F]" data-id="element-450">
                                  {pick(menuStrings.askFinance)}
                                </Button>
                              </Link>
                            </div>
                          </div>
                          {/* Single finance block */}
                          <div className="mt-6 rounded-xl border border-[#D8C4A8]/70 bg-[#EFE8DE] px-4 py-3.5 space-y-2" data-id="element-451">
                            <p className="text-sm leading-snug text-[#2F4A3F]" data-id="element-452">
                              <span className="font-semibold" data-id="element-453">0%</span>{' '}
                              {pick(menuStrings.finance).replace(/^0% ?/i, '')}
                            </p>
                            <Link to={`/${lang}/contact`} onClick={() => setIsMenuOpen(false)} className="inline-flex items-center text-sm font-medium text-[#2F4A3F] underline decoration-[#D8C4A8] underline-offset-4" data-id="element-454">
                              {pick(menuStrings.askFinance)}
                            </Link>
                          </div>
                        </div>}
                    </div>;
            }
            return <Link key={item.href} to={item.href} className="text-lg font-medium text-charcoal" onClick={() => setIsMenuOpen(false)} data-id="element-455">
                    {item.name}
                  </Link>;
          })}
            </nav>
            <div className="pt-4 border-t border-beige/60 space-y-3" data-id="element-456">
              <a href="tel:+447345197820" className="block" data-id="element-457">
                <Button variant="outline" fullWidth className="gap-2" data-id="element-458">
                  <Phone className="w-4 h-4" data-id="element-459" />
                  07345 197820
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-460">
                <Button variant="whatsapp" fullWidth className="gap-2" data-id="element-461">
                  <MessageCircle className="w-4 h-4" data-id="element-462" />
                  WhatsApp
                </Button>
              </a>
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-463">
                <Button variant="outline" fullWidth className="gap-2" data-id="element-464">
                  <TelegramIcon className="w-4 h-4" data-id="element-465" />
                  Telegram
                </Button>
              </a>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-466">
                <Button fullWidth className="gap-2" data-id="element-467">
                  <Calendar className="w-4 h-4" data-id="element-468" />
                  {t('nav.bookOnline')}
                </Button>
              </a>
            </div>
          </div>
        </div>}
    </header>;
}